import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Keyboard,
  ActivityIndicator,
  Modal,
  Platform,
} from 'react-native';
import axios from 'axios';
import { useNavigation } from '@react-navigation/native';

import BaseWeb from '../components/baseWeb';
import { useLogScreen } from '../../useLogScreen';

export default function LaudoWeb() {
  useLogScreen('DilFormulario');
  const navigation = useNavigation();

  useEffect(() => {
    if (Platform.OS === 'web') {
      setTimeout(() => {
        document.title = 'imoGo | Precificador';
      }, 100);
    }
  }, []);

  // Estados de UI
  const [showTipoOptions, setShowTipoOptions] = useState(false);
  const [showCidadeOptions, setShowCidadeOptions] = useState(false);
  const [showBairroOptions, setShowBairroOptions] = useState(false);
  const [showEnderecoOptions, setShowEnderecoOptions] = useState(false);
  // Adicionados: estados para dropdowns opcionais
  const [showQuantidadeQuartosOptions, setShowQuantidadeQuartosOptions] = useState(false);
  const [showQuantidadeSuiteOptions, setShowQuantidadeSuiteOptions] = useState(false);
  const [showQuantidadeVagasOptions, setShowQuantidadeVagasOptions] = useState(false);
  const [showPadraoOptions, setShowPadraoOptions] = useState(false);

  // Campos
  const [tipoImovel, setTipoImovel] = useState('');
  const [cidadeImovel, setCidadeImovel] = useState('');
  const [bairroImovel, setBairroImovel] = useState('');
  const [enderecoImovel, setEnderecoImovel] = useState('');
  const [quantidadeQuartosImovel, setQuantidadeQuartosImovel] = useState('');
  const [quantidadeSuiteImovel, setQuantidadeSuiteImovel] = useState('');
  const [quantidadeVagasImovel, setQuantidadeVagasImovel] = useState('');
  const [padraoImovel, setPadraoImovel] = useState('');
  const [metragem, setMetragem] = useState('');

  // Dados auxiliares
  const enderecoInputRef = useRef(null);
  const metragemInputRef = useRef(null);
  const [dadosLocalizacao, setDadosLocalizacao] = useState(null);
  const [tiposDisponiveis, setTiposDisponiveis] = useState(null);
  const [bairrosDisponiveis, setBairrosDisponiveis] = useState([]);
  const [enderecosDisponiveis, setEnderecosDisponiveis] = useState([]);

  // Loading / submit
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const API_BASE = 'https://gateway-laudo.vercel.app/api/laudo/enderecos/df';
  const API_TIPOS = 'https://gateway-laudo.vercel.app/api/laudo/tipos';
  const API_ESTIMATIVA = 'https://gateway-laudo.vercel.app/api/laudo/estimativa';

  const getLoc = async () => {
    try {
      const { data } = await axios.get(API_BASE, { timeout: 10000 });
      return data;
    } catch (err) {
      console.error('Erro ao buscar endereços:', err?.message || err);
      return null;
    }
  };

  const getTipos = async () => {
    try {
      const { data } = await axios.get(API_TIPOS, { timeout: 10000 });
      if (data && Array.isArray(data.tipos)) return data.tipos;
      console.warn('Resposta /tipos inesperada', data);
      return null;
    } catch (err) {
      console.error('Erro ao buscar tipos:', err?.message || err);
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoadingData(true);
      try {
        const [loc, tipos] = await Promise.all([getLoc(), getTipos()]);
        if (!mounted) return;

        // loc retorna um objeto: { [CIDADE]: { [BAIRRO]: [enderecos...] } }
        if (loc && typeof loc === 'object') {
          setDadosLocalizacao(loc);
        } else {
          setDadosLocalizacao({});
        }

        if (Array.isArray(tipos)) {
          setTiposDisponiveis(tipos);
        } else {
          setTiposDisponiveis([]);
        }
      } finally {
        if (mounted) setIsLoadingData(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const isFormValid = () =>
    Boolean(
      tipoImovel &&
        cidadeImovel &&
        bairroImovel &&
        enderecoImovel &&
        padraoImovel &&
        metragem
    );

  const toIntFromOption = (val) => {
    if (!val) return 0;
    const m = String(val).match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  };

  const mapPadraoToApi = (label) => {
    if (!label) return null;
    const txt = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
    if (txt.startsWith('ORIGINAL')) return 'Original';
    if (txt.startsWith('IMÓVEL PADRÃO') || txt.startsWith('IMOVEL PADRAO')) return 'Padrão';
    if (txt.startsWith('REFORMADO')) return 'Reformado';
    return 'Padrão';
  };

  const handleGerarLaudo = async () => {
    if (!isFormValid()) {
      alert('Preencha todos os campos obrigatórios.');
      return;
    }

    const metragemNum = parseFloat(String(metragem).replace(',', '.'));
    if (!metragemNum || Number.isNaN(metragemNum) || metragemNum <= 0) {
      alert('Informe uma metragem válida.');
      return;
    }

    const params = {
      cidade: cidadeImovel,
      bairro: bairroImovel,
      endereco: enderecoImovel,
      tipo: tipoImovel,
      limite: 20,
      quartos: toIntFromOption(quantidadeQuartosImovel),
      vagas: toIntFromOption(quantidadeVagasImovel),
      suites: toIntFromOption(quantidadeSuiteImovel),
      metragem: metragemNum,
      estado_conservacao: mapPadraoToApi(padraoImovel),
      tolerancia_m2_pct: 0.1,
      tipo_negocio: 'Venda',
    };

    setIsSubmitting(true);
    setLoading(true);
    try {
      const { data, status } = await axios.get(API_ESTIMATIVA, {
        params,
        headers: { accept: 'application/json' },
        timeout: 15000,
      });

      if (status === 200) {
        // Ajuste o nome da rota se sua stack web usar outra tela de resultado
        navigation.navigate('ResultadoDILWeb', { resultado: data });
      } else {
        alert('Não foi possível obter a estimativa.');
      }
    } catch (err) {
      console.error('Erro na estimativa:', err?.message || err);
      alert('Falha ao consultar a API de estimativa.');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const CIDADES_DF = [
    'AGUAS CLARAS',
    'ALPHAVILLE',
    'BRASILIA',
    'BRAZLANDIA',
    'CANDANGOLANDIA',
    'CEILANDIA',
    'CRUZEIRO',
    'GAMA',
    'GUARA',
    'NUCLEO BANDEIRANTE',
    'PARANOA',
    'PLANALTINA',
    'RECANTO DAS EMAS',
    'RIACHO FUNDO',
    'SAMAMBAIA',
    'SANTA MARIA',
    'SAO SEBASTIAO',
    'SETOR INDUSTRIAL',
    'SOBRADINHO',
    'TAGUATINGA',
    'VARJAO',
    'VICENTE PIRES',
    'VILA ESTRUTURAL',
  ];

  return (
    <BaseWeb>
      {(userInfo) => (
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          enabled={Platform.OS !== 'web'}
        >
          <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
            <ScrollView contentContainerStyle={styles.contentMinimal}>
              <View style={styles.formContainer}>
                <Text style={styles.title}>Precificador</Text>
                <Text style={styles.subtitle}>Avaliação precisa (BETA)</Text>

                {isLoadingData ? (
                  <View style={{ alignItems: 'center', paddingVertical: 40 }}>
                    <ActivityIndicator size="large" color="#730d83" />
                    <Text style={{ marginTop: 12, color: '#1F2024' }}>Carregando dados...</Text>
                  </View>
                ) : (
                  <>
                    {/* Tipo de imóvel */}
                    <View style={styles.row}>
                      <Text style={styles.subLabel}>Tipo de imóvel *</Text>
                      <TouchableWithoutFeedback onPress={() => setShowTipoOptions((v) => !v)}>
                        <View style={[styles.areaInput, { justifyContent: 'center' }]}>
                          <Text style={{ color: tipoImovel ? '#1F2024' : '#A9A9A9' }}>
                            {tipoImovel || 'Selecionar'}
                          </Text>
                        </View>
                      </TouchableWithoutFeedback>

                      {showTipoOptions && (
                        <View style={styles.optionsContainer}>
                          {(Array.isArray(tiposDisponiveis) && tiposDisponiveis.length > 0
                            ? tiposDisponiveis.map((t) => (t.tipo || '').toUpperCase())
                            : [
                                'APARTAMENTO',
                                'KITNET/STUDIO',
                                'FLAT/APART-HOTEL',
                                'CASA',
                                'RURAL',
                                'LOTE',
                                'PRÉDIO',
                                'LOJA',
                                'SALA COMERCIAL',
                                'GALPÃO',
                              ]
                          ).map((option) => (
                            <TouchableOpacity
                              key={option}
                              style={styles.optionItem}
                              onPress={() => {
                                setTipoImovel(option);
                                setShowTipoOptions(false);
                              }}
                            >
                              <Text style={styles.optionText}>{option}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                    </View>

                    {/* Cidade */}
                    <View style={styles.row}>
                      <Text style={styles.subLabel}>Cidade *</Text>
                      <TouchableWithoutFeedback onPress={() => setShowCidadeOptions((v) => !v)}>
                        <View style={[styles.areaInput, { justifyContent: 'center' }]}>
                          <Text style={{ color: cidadeImovel ? '#1F2024' : '#A9A9A9' }}>
                            {cidadeImovel || 'Selecionar'}
                          </Text>
                        </View>
                      </TouchableWithoutFeedback>

                      {showCidadeOptions && (
                        <View style={styles.optionsContainer}>
                          {CIDADES_DF.map((option) => (
                            <TouchableOpacity
                              key={option}
                              style={styles.optionItem}
                              onPress={() => {
                                setCidadeImovel(option);
                                setShowCidadeOptions(false);
                                setBairroImovel('');
                                setEnderecoImovel('');
                                setBairrosDisponiveis(
                                  Object.keys((dadosLocalizacao?.[option]) || {})
                                );
                                setEnderecosDisponiveis([]);
                              }}
                            >
                              <Text style={styles.optionText}>{option}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                    </View>

                    {/* Bairro */}
                    <View style={styles.row}>
                      <Text style={styles.subLabel}>Bairro *</Text>
                      <TouchableWithoutFeedback onPress={() => setShowBairroOptions((v) => !v)}>
                        <View style={[styles.areaInput, { justifyContent: 'center' }]}>
                          <Text style={{ color: bairroImovel ? '#1F2024' : '#A9A9A9' }}>
                            {bairroImovel || 'Selecionar'}
                          </Text>
                        </View>
                      </TouchableWithoutFeedback>

                      {showBairroOptions && (
                        <View style={styles.optionsContainer}>
                          {bairrosDisponiveis.map((option) => (
                            <TouchableOpacity
                              key={option}
                              style={styles.optionItem}
                              onPress={() => {
                                setBairroImovel(option);
                                setShowBairroOptions(false);
                                const enderecos =
                                  dadosLocalizacao?.[cidadeImovel]?.[option] || [];
                                setEnderecosDisponiveis(enderecos);
                                setEnderecoImovel('');
                              }}
                            >
                              <Text style={styles.optionText}>{option}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                    </View>

                    {/* Endereço com sugestões */}
                    <View style={styles.row}>
                      <Text style={styles.subLabel}>Endereço *</Text>
                      <TouchableWithoutFeedback onPress={() => enderecoInputRef.current?.focus()}>
                        <View style={styles.inputContainer}>
                          <TextInput
                            ref={enderecoInputRef}
                            style={[styles.areaInput]}
                            placeholder="Digite o endereço (sugestões aparecerão abaixo)"
                            placeholderTextColor="#A9A9A9"
                            value={enderecoImovel}
                            onFocus={() => setShowEnderecoOptions(true)}
                            onChangeText={(text) => {
                              setEnderecoImovel(text);
                              setShowEnderecoOptions(true);
                            }}
                            returnKeyType="done"
                          />
                        </View>
                      </TouchableWithoutFeedback>

                      {showEnderecoOptions && (
                        <View style={styles.optionsContainer}>
                          {Array.isArray(enderecosDisponiveis) && enderecosDisponiveis.length > 0 ? (
                            enderecosDisponiveis
                              .filter((opt) => {
                                if (!enderecoImovel) return true;
                                return opt.toLowerCase().includes(enderecoImovel.toLowerCase());
                              })
                              .slice(0, 50)
                              .map((option) => (
                                <TouchableOpacity
                                  key={option}
                                  style={styles.optionItem}
                                  onPress={() => {
                                    setEnderecoImovel(option);
                                    setShowEnderecoOptions(false);
                                  }}
                                >
                                  <Text style={styles.optionText}>{option}</Text>
                                </TouchableOpacity>
                              ))
                          ) : (
                            <View style={styles.optionItem}>
                              <Text style={[styles.optionText, { color: '#7A7A7A' }]}>
                                {cidadeImovel && bairroImovel
                                  ? 'Nenhum endereço disponível'
                                  : 'Selecione cidade e bairro primeiro'}
                              </Text>
                            </View>
                          )}
                        </View>
                      )}
                    </View>

                    <View style={styles.divider} />

                    {/* Quantidades opcionais */}
                    <View style={styles.row}>
                      <Text style={styles.subLabel}>Nº de quartos (opcional)</Text>
                      <TouchableWithoutFeedback
                        onPress={() =>
                          setShowQuantidadeQuartosOptions((v) => !v)
                        }
                      >
                        <View style={[styles.areaInput, { justifyContent: 'center' }]}>
                          <Text
                            style={{ color: quantidadeQuartosImovel ? '#1F2024' : '#A9A9A9' }}
                          >
                            {quantidadeQuartosImovel || 'Selecionar'}
                          </Text>
                        </View>
                      </TouchableWithoutFeedback>
                      {showQuantidadeQuartosOptions && (
                        <View style={styles.optionsContainer}>
                          {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map((option) => (
                            <TouchableOpacity
                              key={option}
                              style={styles.optionItem}
                              onPress={() => {
                                setQuantidadeQuartosImovel(option);
                                setShowQuantidadeQuartosOptions(false);
                              }}
                            >
                              <Text style={styles.optionText}>{option}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                    </View>

                    <View style={styles.row}>
                      <Text style={styles.subLabel}>Nº de suítes (opcional)</Text>
                      <TouchableWithoutFeedback
                        onPress={() => setShowQuantidadeSuiteOptions((v) => !v)}
                      >
                        <View style={[styles.areaInput, { justifyContent: 'center' }]}>
                          <Text
                            style={{ color: quantidadeSuiteImovel ? '#1F2024' : '#A9A9A9' }}
                          >
                            {quantidadeSuiteImovel || 'Selecionar'}
                          </Text>
                        </View>
                      </TouchableWithoutFeedback>
                      {showQuantidadeSuiteOptions && (
                        <View style={styles.optionsContainer}>
                          {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map((option) => (
                            <TouchableOpacity
                              key={option}
                              style={styles.optionItem}
                              onPress={() => {
                                setQuantidadeSuiteImovel(option);
                                setShowQuantidadeSuiteOptions(false);
                              }}
                            >
                              <Text style={styles.optionText}>{option}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                    </View>

                    <View style={styles.row}>
                      <Text style={styles.subLabel}>Nº de vagas (opcional)</Text>
                      <TouchableWithoutFeedback
                        onPress={() => setShowQuantidadeVagasOptions((v) => !v)}
                      >
                        <View style={[styles.areaInput, { justifyContent: 'center' }]}>
                          <Text
                            style={{ color: quantidadeVagasImovel ? '#1F2024' : '#A9A9A9' }}
                          >
                            {quantidadeVagasImovel || 'Selecionar'}
                          </Text>
                        </View>
                      </TouchableWithoutFeedback>
                      {showQuantidadeVagasOptions && (
                        <View style={styles.optionsContainer}>
                          {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map((option) => (
                            <TouchableOpacity
                              key={option}
                              style={styles.optionItem}
                              onPress={() => {
                                setQuantidadeVagasImovel(option);
                                setShowQuantidadeVagasOptions(false);
                              }}
                            >
                              <Text style={styles.optionText}>{option}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                    </View>

                    {/* Metragem */}
                    <View style={styles.row}>
                      <Text style={styles.subLabel}>Metragem útil *</Text>
                      <TouchableWithoutFeedback onPress={() => metragemInputRef.current?.focus()}>
                        <View style={styles.inputContainer}>
                          <TextInput
                            ref={metragemInputRef}
                            keyboardType="number-pad"
                            style={styles.areaInput}
                            placeholder="Informar a metragem útil em m²"
                            placeholderTextColor="#A9A9A9"
                            value={metragem}
                            onChangeText={setMetragem}
                          />
                        </View>
                      </TouchableWithoutFeedback>
                    </View>

                    {/* Padrão */}
                    <View style={styles.row}>
                      <Text style={styles.subLabel}>Padrão atual do imóvel *</Text>
                      <TouchableWithoutFeedback onPress={() => setShowPadraoOptions((v) => !v)}>
                        <View style={[styles.areaInput, { justifyContent: 'center' }]}>
                          <Text style={{ color: padraoImovel ? '#1F2024' : '#A9A9A9' }}>
                            {padraoImovel || 'Selecionar'}
                          </Text>
                        </View>
                      </TouchableWithoutFeedback>

                      {showPadraoOptions && (
                        <View style={styles.optionsContainer}>
                          {[
                            'ORIGINAL, REQUER MUITOS REPAROS',
                            'IMÓVEL PADRÃO, COM MANUTENÇÃO EM DIA',
                            'REFORMADO, EM EXCELENTE ESTADO',
                          ].map((option) => (
                            <TouchableOpacity
                              key={option}
                              style={styles.optionItem}
                              onPress={() => {
                                setPadraoImovel(option);
                                setShowPadraoOptions(false);
                              }}
                            >
                              <Text style={styles.optionText}>{option}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                    </View>

                    <View style={styles.divider} />

                    {/* Botões */}
                    <View style={styles.buttonContainer}>
                      <TouchableOpacity
                        style={[
                          styles.saveButton,
                          (!isFormValid() || isSubmitting) && { backgroundColor: '#ccc' },
                        ]}
                        onPress={handleGerarLaudo}
                        disabled={!isFormValid() || isSubmitting}
                      >
                        <Text style={styles.saveButtonText}>
                          {isSubmitting ? 'Enviando...' : 'Gerar Laudo'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </>
                )}

                <Modal transparent animationType="fade" visible={loading}>
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="white" />
                    <Text style={styles.loadingText}>Gerando Laudo...</Text>
                  </View>
                </Modal>
              </View>
            </ScrollView>
          </TouchableWithoutFeedback>
        </KeyboardAvoidingView>
      )}
    </BaseWeb>
  );
}

const styles = {
  contentMinimal: {
    paddingVertical: 20,
    paddingHorizontal: 15,
    alignItems: 'center',
  },
  formContainer: {
    width: '100%',
    maxWidth: 860,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1F2024',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2024',
    marginBottom: 16,
  },
  row: {
    marginBottom: 18,
    width: '100%',
  },
  subLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2024',
    marginBottom: 8,
  },
  inputContainer: {
    width: '100%',
  },
  areaInput: {
    borderWidth: 1,
    borderColor: '#D3D3D3',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    fontSize: 15,
    backgroundColor: '#f5f5f5',
  },
  divider: {
    height: 1,
    backgroundColor: '#E0E0E0',
    marginVertical: 16,
    width: '100%',
  },
  buttonContainer: {
    marginTop: 24,
    alignItems: 'center',
  },
  saveButton: {
    backgroundColor: '#730d83',
    paddingVertical: 14,
    paddingHorizontal: 40,
    borderRadius: 24,
    alignItems: 'center',
    marginBottom: 8,
  },
  saveButtonText: {
    color: '#f5f5f5',
    fontSize: 16,
    fontWeight: '600',
  },
  // Select options
  optionsContainer: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    borderWidth: 1,
    borderRadius: 10,
    marginTop: 6,
  },
  optionItem: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  optionText: {
    fontSize: 15,
    color: '#1F2024',
  },
  // Loading modal
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  loadingText: {
    color: '#f5f5f5',
    marginTop: 10,
    fontSize: 16,
  },
};